import { fazerLogout } from '../utils/logout';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/Pedidos.css';
import '../styles/Dashboard.css';
import '../styles/Perfil.css';
import Logo from './Logo';
import { getAuthSession } from '../utils/authStorage';

// Cache com TTL (5 minutos)
const CACHE_TTL = 5 * 60 * 1000;

function salvarCacheCliente(customerId, data) {
  try {
    const cache = JSON.parse(sessionStorage.getItem('perfilClienteCache') || '{}');
    cache[customerId] = {
      data,
      timestamp: Date.now(),
    };
    sessionStorage.setItem('perfilClienteCache', JSON.stringify(cache));
  } catch {
    // Ignora erros de storage
  }
}

function obterCacheCliente(customerId) {
  try {
    const cache = JSON.parse(sessionStorage.getItem('perfilClienteCache') || '{}');
    const entry = cache[customerId];
    if (!entry) return null;
    
    const idade = Date.now() - entry.timestamp;
    if (idade > CACHE_TTL) {
      delete cache[customerId];
      sessionStorage.setItem('perfilClienteCache', JSON.stringify(cache));
      return null;
    }
    
    return entry.data;
  } catch {
    return null;
  }
}

function Perfil() {
  const [cliente,     setCliente]     = useState(null);
  const [clienteNome, setClienteNome] = useState('');
  const [carregando,  setCarregando]  = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const { token, customerId, customerName } = getAuthSession();

    console.log('[PERFIL] Debug - Token:', token ? 'EXISTS' : 'NULL', 'ClienteId:', customerId, 'Nome:', customerName);

    if (!customerId || !token) { 
      console.log('[PERFIL] Redirecionando - falta token ou clienteId');
      navigate('/'); 
      return; 
    }
    setClienteNome(customerName);

    // Tenta carregar do cache primeiro
    const cachedCliente = obterCacheCliente(customerId);
    if (cachedCliente) {
      console.log('[PERFIL] Carregando do cache');
      if (cachedCliente.ok !== false) setCliente(cachedCliente);
      setCarregando(false);
      return;
    }

    const url = '/api/customer/' + customerId;
    const headers = { 'Authorization': 'Bearer ' + token };
    
    console.log('[PERFIL] Chamando API:', url, 'Headers:', headers);

    fetch(url, {
      headers: headers,
    })
      .then(r => {
        console.log('[PERFIL] Resposta status:', r.status);
        return r.json();
      })
      .then(data => {
        console.log('[PERFIL] Dados recebidos:', data);
        salvarCacheCliente(customerId, data);
        if (data.ok !== false) setCliente(data);
      })
      .catch(error => {
        console.error('[PERFIL] Erro na chamada:', error);
      })
      .finally(() => setCarregando(false));
  }, [navigate]);

  const handleLogout = () => fazerLogout(navigate);

  const formatarDoc = (doc) => {
    if (!doc) return '—';
    const s = String(doc).replace(/\D/g, '');
    if (s.length === 14) return s.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
    if (s.length === 11) return s.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
    return doc;
  };

  const HeaderNav = () => (
    <header className="dashboard-header">
      <div className="header-left">
        <Logo size="small" onClick={() => navigate('/dashboard')} />
        <div className="header-text">
          <h1>Perfil da Conta</h1>
          <p className="cliente-info">{clienteNome || 'Carregando...'}</p>
        </div>
      </div>
      <nav className="nav-buttons">
        <button onClick={() => navigate('/dashboard')} className="btn-nav">Dashboard</button>
        <button onClick={() => navigate('/pedidos')}   className="btn-nav">Pedidos</button>
                <button onClick={handleLogout}                 className="btn-logout">Sair</button>
      </nav>
    </header>
  );

  if (carregando) return (
    <div className="pedidos-container">
      <HeaderNav />
      <div className="pedidos-content">
        <div className="loading">Carregando dados do perfil...</div>
      </div>
    </div>
  );

  return (
    <div className="pedidos-container">
      <HeaderNav />
      <div className="pedidos-content">
        <div className="perfil-container">
          <div className="perfil-header">
            <h2>Dados da Conta</h2>
            <p>Informações cadastrais do cliente</p>
          </div>

          <div className="perfil-cards">
            {/* Dados Empresariais */}
            <div className="perfil-card">
              <div className="perfil-card-header">
                <span className="perfil-icon">🏢</span>
                <h3>Dados Empresariais</h3>
              </div>
              <div className="perfil-card-content">
                {[
                  ['Razão Social',       cliente?.name],
                  ['Nome Fantasia',      cliente?.tradeName],
                  ['CNPJ/CPF',          formatarDoc(cliente?.personIdentificationNumber)],
                  ['Inscrição Estadual', cliente?.stateInscription],
                  ['ID do Cliente',      cliente?.id ? '#' + cliente.id : null],
                ].filter(([, v]) => v).map(([label, valor]) => (
                  <div key={label} className="info-item">
                    <label>{label}:</label>
                    <p>{valor}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Contato */}
            <div className="perfil-card">
              <div className="perfil-card-header">
                <span className="perfil-icon">📧</span>
                <h3>Contato</h3>
              </div>
              <div className="perfil-card-content">
                {[
                  ['E-mail',           cliente?.email],
                  ['E-mail NFe',       cliente?.emailNfe],
                  ['Telefone',         cliente?.corporatePhone || cliente?.phone],
                  ['Tel. Cobrança',    cliente?.billingPhone],
                  ['Tel. Entrega',     cliente?.deliveryPhone],
                ].filter(([, v]) => v).map(([label, valor]) => (
                  <div key={label} className="info-item">
                    <label>{label}:</label>
                    <p>{valor}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Endereço */}
            <div className="perfil-card">
              <div className="perfil-card-header">
                <span className="perfil-icon">📍</span>
                <h3>Endereço Comercial</h3>
              </div>
              <div className="perfil-card-content">
                {[
                  ['Endereço',    cliente?.commercialAddress
                    ? `${cliente.commercialAddress}${cliente.commercialAddressNumber ? ', ' + cliente.commercialAddressNumber : ''}`
                    : null],
                  ['Bairro',      cliente?.businessDistrict],
                  ['Cidade',      cliente?.businessCity],
                  ['CEP',         cliente?.commercialZipCode],
                ].filter(([, v]) => v).map(([label, valor]) => (
                  <div key={label} className="info-item">
                    <label>{label}:</label>
                    <p>{valor}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Endereço de Entrega (se existir e diferente) */}
            {cliente?.deliveryAddresses && cliente.deliveryAddresses.length > 0 && (
              <div className="perfil-card">
                <div className="perfil-card-header">
                  <span className="perfil-icon">🚚</span>
                  <h3>Endereço de Entrega</h3>
                </div>
                <div className="perfil-card-content">
                  {cliente.deliveryAddresses.slice(0, 1).map((addr, i) => (
                    <div key={i}>
                      {[
                        ['Destinatário', addr.receiverName],
                        ['Endereço',     addr.address ? `${addr.address}${addr.number ? ', ' + addr.number : ''}` : null],
                        ['Bairro',       addr.district],
                        ['Cidade',       addr.city],
                        ['Estado',       addr.state],
                        ['CEP',          addr.zipCode],
                      ].filter(([, v]) => v).map(([label, valor]) => (
                        <div key={label} className="info-item">
                          <label>{label}:</label>
                          <p>{valor}</p>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Perfil;
